import * as mobx from "mobx";
import moment from "moment";
import LoginStore from "./LoginStore";
import UserActivityStore from "./UserActivityStore";

/**
 * call this function to start a monitor that keeps the session alive by regularly extending the session
 * as it gets closer to expiration, but only while the user is active.
 * @param {function} extendSession - function that will extend the session and returns a promise of type {isValid: boolean, inactivityTimeout: string}
 * @param {function} isSessionActive - function that references observables and returns a boolean of whether the session is active.
 * @param {function} getTokenTimeout - function that returns a session inactivity timestamp if any
 * @returns {function} that may be called to stop the heartbeat
 */
export default function startSessionActivityHeartbeat() {
  let timer;

  function scheduleRenewalIn(milliseconds) {
    timer = setTimeout(async () => {
      try {
        const { inactivityTimeout, isValid } = await LoginStore.extendSession();
        if (inactivityTimeout) {
          const delay = chooseDelay(inactivityTimeout);
          scheduleRenewalIn(delay);
        } else if (!isValid) {
          console.warn("session no longer active");
        }
      } catch (er) {
        console.error("unable to extend session. Retrying in a minute", er);
        scheduleRenewalIn(60000);
      }
    }, milliseconds);
  }

  /**
   * determines a good delay before extending the session again.
   * If the expiration is near (minutes away), sends frequent requests to ensure 
   * the session doesn't expire from time drift or latency,
   * otherwise it sends much less frequent updates.
   * @param {string} timestamp - timestamp that the token will expire
   */
  function chooseDelay(timestamp) {
    const msUntilExpiry = moment(timestamp).valueOf() - Date.now();
    // if expiration is very near (<2 minutes) every 10s until further out)
    // otherwise schedule halfway before expiration, --but at least 60s out, and at most an hour out
    const delay = msUntilExpiry < 120000 
      ? 10000 
      : Math.max(60000, Math.min(msUntilExpiry / 2, 1000 * 60 * 60));
    return delay;
  }

  const disposer = mobx.reaction(
    () => LoginStore.isLoggedIn && UserActivityStore.isActive,
    (shouldKeepAlive) => {
      if (shouldKeepAlive) {
        if (!LoginStore.tokenInactivityTimeout) {
          scheduleRenewalIn(0);
        } else {
          scheduleRenewalIn(10000);
        }
      } else {
        clearTimeout(timer);
      }
    },
    { fireImmediately: true }
  );

  return () => {
    disposer();
    clearTimeout(timer);
  }
}